import React, { useCallback, useContext, useEffect, useState } from 'react'
import { navigate, RouteComponentProps } from '@reach/router'
import PageHeading from '../Common/PageHeading'
import { ApiFetch, ApiPut } from '../Common/Api'
import Paginator from '../Common/Table/Paginator'
import type InAppNotification from '../../models/InAppNotification'
import type { NotificationFilterValue } from '../../models/InAppNotification'
import type InAppNotificationResponse from '../../models/InAppNotificationResponse'
import { displayNotificationTimeAgo } from '../../utilities'
import UserContext, { Roles } from '../../UserContext'
import { ProviderCommunicationPreferencesEnum } from '../../models/enums/CommunicationPreferences'
import {
    DEFAULT_NOTIFICATION_FILTER,
    getNotificationFilters,
    hasFilterOption
} from './filterCatalog'
import { getNotificationTargetPath } from './navigation'
import { SaveEvent, SelectEvent } from '../Common/AnalyticsEvents'

const DEFAULT_PAGE_SIZE = 10

type UserCommunicationPreferences = {
    communicationMethods?: string[]
}

const Notifications: React.FC<RouteComponentProps> = () => {
    const userContext = useContext(UserContext)
    const { hasAnyRole } = userContext
    const isProvider = hasAnyRole(Roles.Provider)
    const isCareManager = hasAnyRole(Roles.WaiverServiceCoordinator, Roles.WaiverServiceLeader, Roles.MarketAdmin)
    const availableFilters = getNotificationFilters(isProvider, isCareManager)
    const [loading, setLoading] = useState(false)
    const [isPreferenceLoading, setIsPreferenceLoading] = useState(true)
    const [isInAppEnabled, setIsInAppEnabled] = useState(false)
    const [notifications, setNotifications] = useState<InAppNotification[]>([])
    const [selectedFilter, setSelectedFilter] = useState<NotificationFilterValue>(DEFAULT_NOTIFICATION_FILTER)
    const [rowIndex, setRowIndex] = useState(0)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)

    useEffect(() => {
        const endpoint = isProvider
            ? 'User/ProviderPreferences'
            : isCareManager
                ? 'User/CareManagerPreferences'
                : null

        if (!endpoint) {
            setIsInAppEnabled(false)
            setIsPreferenceLoading(false)
            return
        }

        setIsPreferenceLoading(true)
        ApiFetch<UserCommunicationPreferences>(endpoint)
            .then((result) => {
                const methods = result?.communicationMethods || []
                setIsInAppEnabled(methods.includes(ProviderCommunicationPreferencesEnum.InAppNotifications))
            })
            .catch(() => setIsInAppEnabled(false))
            .finally(() => setIsPreferenceLoading(false))
    }, [isCareManager, isProvider])

    useEffect(() => {
        if (hasFilterOption(availableFilters, selectedFilter)) return

        setSelectedFilter(availableFilters[0].value)
        setRowIndex(0)
    }, [availableFilters, selectedFilter])

    const loadNotifications = useCallback(() => {
        if (!isInAppEnabled) return
        setLoading(true)
        const page = Math.floor(rowIndex / pageSize) + 1
        ApiFetch<InAppNotificationResponse>('Notification/InApp', { filter: selectedFilter, page, pageSize })
            .then((result) => {
                setNotifications(result.notifications || [])
                setTotalCount(result.totalCount || 0)
            })
            // eslint-disable-next-line no-console
            .catch(console.log)
            .finally(() => setLoading(false))
    }, [isInAppEnabled, selectedFilter, rowIndex, pageSize])

    useEffect(() => {
        if (!isInAppEnabled) return
        loadNotifications()
    }, [isInAppEnabled, loadNotifications])

    const goToNotification = (notification: InAppNotification) => {
        const goTo = getNotificationTargetPath(notification)

        const onNavigate = () => {
            void navigate(goTo)
        }

        if (!notification.isRead) {
            ApiPut<boolean>(`Notification/${notification.notificationID}/Viewed`)
                .then(() => {
                    if (selectedFilter === 'unread') {
                        setNotifications((items) => items.filter((item) => item.notificationID !== notification.notificationID))
                        setTotalCount((count) => Math.max(0, count - 1))
                    } else {
                        setNotifications((items) => items.map((item) => item.notificationID === notification.notificationID ? { ...item, isRead: true } : item))
                    }
                    onNavigate()
                })
                .catch(onNavigate)
            return
        }

        onNavigate()
    }

    return (
        <>
            <PageHeading pageTitle='Notifications' createButton={false} filterButton={false} />
            <div className='notifications-page'>
                {isPreferenceLoading && <div className='notifications-empty'>Loading notifications...</div>}
                {!isPreferenceLoading && !isInAppEnabled && (
                    <div className='notifications-empty'>Enable In-App Notifications in User Preferences to view notifications in the app.</div>
                )}
                {!isPreferenceLoading && isInAppEnabled && (
                    <>
                <div className='notifications-filter-row'>
                    <select
                        aria-label='Filter notifications'
                        className='notifications-filter-select'
                        value={selectedFilter}
                        onChange={(e) => {
                            SaveEvent(SelectEvent(userContext, 'Notifications', `Filter_${e.target.value}`))
                            setSelectedFilter(e.target.value as NotificationFilterValue)
                            setRowIndex(0)
                        }}
                    >
                        {availableFilters.map((filter) => (
                            <option key={filter.value} value={filter.value}>{filter.label}</option>
                        ))}
                    </select>
                </div>
                {loading && <div className='notifications-empty'>Loading notifications...</div>}
                {!loading && notifications.length === 0 && (
                    <div className='notifications-empty'>
                        No notifications.You're all caught up <span aria-hidden='true'>✓</span>

                    </div>
                )}
                {!loading && notifications.length > 0 && (
                    <ul className='notifications-list'>
                        {notifications.map((notification) => (
                            <li key={notification.notificationID}>
                                <button
                                    type='button'
                                    className='notifications-list-item'
                                    onClick={() => goToNotification(notification)}
                                    aria-label={`Open notification ${notification.title}`}
                                >
                                    <span className='notification-main'>
                                        <span className={`notification-indicator ${notification.isRead ? 'read' : 'unread'}`} aria-hidden='true'></span>
                                        <span className='notification-body'>
                                            <span className='notification-title'>{notification.title}</span>
                                            <span className='notification-time'>{displayNotificationTimeAgo(notification.createdOn)}</span>
                                        </span>
                                    </span>
                                    <span className='notification-action'>View Referral</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {!loading && totalCount > 0 && (
                    <Paginator
                        defaultPageSize={DEFAULT_PAGE_SIZE}
                        currentPage={Math.floor(rowIndex / pageSize) + 1}
                        total={totalCount}
                        onChange={(page, selectedPageSize) => {
                            setPageSize(selectedPageSize)
                            setRowIndex((page - 1) * selectedPageSize)
                        }}
                    />
                )}
                    </>
                )}
            </div>
        </>
    )
}

export default Notifications
